import type { TestStep } from '../api/tests';

/**
 * What a guided step is about, for the icon beside it: a tester reads a
 * route of pictures before a list of sentences (the owner, 2026-09-24). A
 * step may say so itself (`phase`); otherwise its own words decide, and
 * "look" is what is left.
 */
export type Phase =
  | 'watcher'
  | 'join'
  | 'base'
  | 'map'
  | 'radio'
  | 'transport'
  | 'return'
  | 'objective'
  | 'fight'
  | 'look';

export const PHASES: Phase[] = [
  'watcher',
  'join',
  'base',
  'map',
  'radio',
  'transport',
  'return',
  'objective',
  'fight',
  'look',
];

export const PHASE_ICON: Record<Phase, string> = {
  watcher: 'bi-pc-display',
  join: 'bi-door-open',
  base: 'bi-house-door',
  map: 'bi-map',
  radio: 'bi-broadcast',
  transport: 'bi-airplane',
  return: 'bi-arrow-return-left',
  objective: 'bi-check2-circle',
  fight: 'bi-crosshair',
  look: 'bi-eye',
};

// In this order: the first rule that matches names the phase.
const RULES: [Phase, RegExp][] = [
  ['watcher', /watcher|icona/i],
  [
    'join',
    /has entrat|entres viu|espectador|viu al teu lloc|el teu lloc|lobby|el segell|entra al servidor|entra a la (partida|sessi[óo])|tria un lloc/i,
  ],
  ['map', /\bmapa\b|\bmarca\b|\bmarques\b/i],
  ['radio', /r[àa]dio|\bprc\b|simplex|men[úu] de suports|\bsuports\b/i],
  ['transport', /helic[òo]pter|transport|\bpilot\b|heliport|\bvehicle/i],
  ['return', /\btorna\b|tornar|extracci[óo]|acabar sola|s’acaba|s'acaba/i],
  ['objective', /objectiu|complert|complerta|\btasca\b/i],
  ['fight', /enemic|facci[óo]|combat|patrulla|neteja|abat|guarnici[óo]|resist/i],
  [
    'base',
    /\bbase\b|edifici|moble|llitera|arsenal|caixa|tenda|campament|pissarra|cadir|taula|bandera/i,
  ],
];

export function phaseOf(step: TestStep): Phase {
  if (step.phase && (PHASES as string[]).includes(step.phase)) return step.phase as Phase;
  const words = `${step.text} ${step.expect ?? ''}`;
  for (const [phase, rule] of RULES) if (rule.test(words)) return phase;
  return 'look';
}

/** How far a task has come, as the stage a person is at; -1 for a cancelled one. */
export function stageOf(kind: string, state: string, steps: number, answered: number): number {
  if (state === 'cancelled') return -1;
  if (kind === 'play') {
    if (state === 'open') return 0;
    if (state === 'claimed') return answered === 0 ? 1 : answered < steps ? 2 : 3;
    if (state === 'done') return 3;
    return 4;
  }
  if (kind === 'lend') {
    if (state === 'open') return 0;
    if (state === 'claimed') return 1;
    if (state === 'done') return 2;
    return 3;
  }
  if (state === 'open' || state === 'claimed') return 0;
  if (state === 'done') return 1;
  return 2;
}
